import { ComponentProps, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Header, HeaderLabel } from '@backstage/core-components';
import { useRouteRefParams } from '@backstage/core-plugin-api';
import { DEFAULT_NAMESPACE, RELATION_OWNED_BY } from '@backstage/catalog-model';
import {
  EntityDisplayName,
  EntityRefLinks,
  FavoriteEntity,
  InspectEntityDialog,
  entityRouteRef,
  getEntityRelations,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';

type InspectTab = ComponentProps<typeof InspectEntityDialog>['initialTab'];

/**
 * Wave-style header for the new frontend system entity page.
 *
 * Since @backstage/plugin-catalog 2.0.7 the entity page renders a BUI header
 * unless an `entity-header` extension supplies a legacy element. Supplying one
 * flips the page back onto the legacy MUI Page/Header/tabs layout. The built-in
 * legacy EntityHeader is not exported, so this reproduces it from public parts.
 */
export const LegacyEntityHeader = () => {
  const { entity } = useAsyncEntity();
  const { kind, namespace, name } = useRouteRefParams(entityRouteRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const displayName = entity?.metadata.title ?? entity?.metadata.name ?? name;
  const ns = entity?.metadata.namespace ?? namespace;
  const title =
    ns && ns !== DEFAULT_NAMESPACE ? `${displayName} in ${ns}` : displayName;
  const specType =
    entity?.spec && 'type' in entity.spec
      ? String(entity.spec.type)
      : undefined;
  const type = specType
    ? `${kind.toLowerCase()} — ${specType.toLowerCase()}`
    : kind.toLowerCase();

  const inspectTab = searchParams.get('inspect');
  const setInspectParam = (value?: string) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === undefined) {
        next.delete('inspect');
      } else {
        next.set('inspect', value);
      }
      return next;
    });
  const ownedBy = entity ? getEntityRelations(entity, RELATION_OWNED_BY) : [];

  return (
    <Header
      pageTitleOverride={title}
      type={type}
      title={
        <Box
          display="inline-flex"
          alignItems="center"
          height="1em"
          maxWidth="100%"
        >
          <Box
            component="span"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
          >
            {entity ? <EntityDisplayName entityRef={entity} hideIcon /> : title}
          </Box>
          {entity && <FavoriteEntity entity={entity} />}
        </Box>
      }
    >
      {entity && (
        <>
          {ownedBy.length > 0 && (
            <HeaderLabel
              label="Owner"
              contentTypograpyRootComponent="p"
              value={
                <EntityRefLinks
                  entityRefs={ownedBy}
                  defaultKind="Group"
                  color="inherit"
                />
              }
            />
          )}
          {entity.spec?.lifecycle && (
            <HeaderLabel
              label="Lifecycle"
              value={String(entity.spec.lifecycle)}
            />
          )}
          <IconButton
            aria-label="more"
            color="inherit"
            onClick={e => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setInspectParam('');
              }}
            >
              Inspect entity
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigator.clipboard.writeText(window.location.toString());
              }}
            >
              Copy entity URL
            </MenuItem>
          </Menu>
          <InspectEntityDialog
            entity={entity}
            initialTab={(inspectTab || undefined) as InspectTab}
            open={typeof inspectTab === 'string'}
            onClose={() => setInspectParam()}
            onSelect={tab => setInspectParam(tab)}
          />
        </>
      )}
    </Header>
  );
};
